import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { EuroDenominationArt } from './euro/EuroDenominationArt';
import { EURO_DENOMINATIONS } from './euro/euroDenominations';

export { EURO_DENOMINATIONS };

const emptyQuantities = () =>
  Object.fromEntries(EURO_DENOMINATIONS.map((d) => [d.id, '']));

const parseQty = (raw) => {
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const formatEuro = (value) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);

/**
 * Conteggio fisico banconote e monete €.
 * @param {function(number): void} onApply - riceve il totale calcolato (2 decimali)
 */
const EuroCashCounterDialog = ({
  open,
  onClose,
  onApply,
  title = 'Conteggio banconote e monete',
  applyHint = 'Il totale verrà copiato nel campo contanti.'
}) => {
  const [quantities, setQuantities] = useState(emptyQuantities);

  useEffect(() => {
    if (open) setQuantities(emptyQuantities());
  }, [open]);

  const total = useMemo(() => {
    return EURO_DENOMINATIONS.reduce((sum, d) => sum + parseQty(quantities[d.id]) * d.value, 0);
  }, [quantities]);

  const handleQtyChange = (id, value) => {
    if (value === '' || /^\d+$/.test(value)) {
      setQuantities((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleCalculate = () => {
    const rounded = Math.round(total * 100) / 100;
    onApply(rounded);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Inserisci il numero di pezzi per ogni taglio. {applyHint}
        </Typography>
        <Grid container spacing={2}>
          {EURO_DENOMINATIONS.map((d) => (
            <Grid item xs={6} sm={4} md={3} key={d.id}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <EuroDenominationArt value={d.value} kind={d.kind} />
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
                    {d.label}
                  </Typography>
                  <TextField
                    label="Quantità"
                    size="small"
                    fullWidth
                    value={quantities[d.id]}
                    onChange={(e) => handleQtyChange(d.id, e.target.value)}
                    inputProps={{ inputMode: 'numeric', min: 0, 'aria-label': `Quantità ${d.label}` }}
                    placeholder="0"
                  />
                  {parseQty(quantities[d.id]) > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                      {formatEuro(parseQty(quantities[d.id]) * d.value)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Totale provvisorio
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {formatEuro(total)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={handleCalculate}>
          Calcola
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EuroCashCounterDialog;
